"use client";

import { useState, useEffect, useCallback } from "react";
import { deleteInfo, getPublicInfo, postInfo } from "utils/api";
import { extractErrorMessage, PaginatedApiResponse, Severity } from "utils/helper/ApiResponses";
import { PredifineItem } from "utils/types/Info.types";


interface SnackbarState {
  open: boolean;
  message: string;
  severity: Severity;
}

// Hook to manage the state and logic for a single predefined list
export function usePredefinedListsManager() {
  const [list, setList] = useState<PredifineItem[]>([]);
  const [subCategory, setSubCategory] = useState<string>(""); // e.g., 'category'
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false); // For inside the dialog
  const [isSaving, setIsSaving] = useState(false); // For add/delete actions
  
  const [snackbar, setSnackbar] = useState<SnackbarState>({
    open: false,
    message: "",
    severity: "success",
  });

  // --- Snackbar Handlers ---
  const handleSnackbarOpen = useCallback((message: string, severity: Severity) => {
    setSnackbar({ open: true, message, severity });
  }, []);

  const handleSnackbarClose = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  // --- Data Fetching ---
  const fetchList = useCallback(async (category: string) => {
    if (!category) return;
    setLoading(true);
    try {
      // Corrected API call: slug is ['predifine', category]
      const result = await getPublicInfo<PredifineItem>(['predifine', category], {
        page: 1,
        limit: 200, // Get all items
        sortBy: 'name',
        order: 'asc',
      });
      
      const data = (result.data as PaginatedApiResponse<PredifineItem>)?.data;
      setList(data || []);
      
    } catch (err: any) {
      console.error(`Error fetching ${category}:`, err);
      handleSnackbarOpen(extractErrorMessage(err).message, "error");
      setList([]); // Clear list on error
    } finally {
      setLoading(false);
    }
  }, [handleSnackbarOpen]);

  // Fetch the list only when the dialog is opened and subCategory is set
  useEffect(() => {
    if (open && subCategory) {
      fetchList(subCategory);
    }
  }, [open, subCategory, fetchList]);

  // --- Dialog and CRUD Handlers ---
  const handleOpenDialog = (category: string) => {
    setSubCategory(category);
    setOpen(true);
  };

  const handleCloseDialog = () => {
    setOpen(false);
    setList([]);
    setSubCategory("");
  };

  const handleSaveItem = async (itemName: string) => {
    if (!itemName.trim()) {
      handleSnackbarOpen("Le nom ne peut pas être vide.", "warning");
      return;
    }
    setIsSaving(true);
    try {
      // Corrected API call: slug is ['predifine', subCategory]
      await postInfo(['predifine', subCategory], { name: itemName });
      handleSnackbarOpen(`${itemName} ajouté avec succès!`, "success");
      fetchList(subCategory); // Refresh the list
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteItem = async (id: string) => {
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Êtes-vous sûr de vouloir supprimer cet élément ?")) return;

    setIsSaving(true);
    try {
      // Corrected API call: slug is ['predifine', subCategory]
      await deleteInfo(['predifine', subCategory], id);
      handleSnackbarOpen("Élément supprimé avec succès!", "success");
      fetchList(subCategory); // Refresh the list
    } catch (error: any) {
      handleSnackbarOpen(extractErrorMessage(error).message, "error");
    } finally {
      setIsSaving(false);
    }
  };

  return {
    list,
    subCategory,
    open,
    loading,
    isSaving,
    snackbar,
    handleOpenDialog,
    handleCloseDialog,
    handleSaveItem,
    handleDeleteItem,
    handleSnackbarClose,
  };
}